// src/services/emails/emailService.js
/**
 * Lightweight email helper that forwards order events to a backend mailer.
 * The actual email delivery (SendGrid/Mailgun/etc.) should be handled server-side.
 *
 * Expected environment variable:
 *   REACT_APP_EMAIL_API_URL=https://<your-cloud-function>/api
 *
 * The service will no-op when the endpoint is not configured so we never block checkout.
 */

const EMAIL_API_BASE =
  process.env.REACT_APP_EMAIL_API_URL ||
  'https://us-central1-pontshoyarona.cloudfunctions.net/api';

const buildEndpoint = (path) => {
  if (!EMAIL_API_BASE) return null;
  return `${EMAIL_API_BASE.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
};

const postEmailRequest = async (path, payload) => {
  const url = buildEndpoint(path);

  if (!url) {
    console.info('[EmailService] Skipping email dispatch – REACT_APP_EMAIL_API_URL not set.');
    return { skipped: true };
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `[EmailService] ${response.status} ${response.statusText} – ${errorText || 'Unknown error'}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error('[EmailService] Failed to dispatch email:', error);
    return { error: error.message };
  }
};

export const EmailService = {
  /**
   * Send customer order confirmation.
   * @param {object} order - The order payload returned from OrderService.
   */
  async sendOrderConfirmation(order) {
    if (!order?.customer?.email) {
      console.warn('[EmailService] Missing customer email on order; skipping confirmation email.');
      return { skipped: true };
    }

    const hasTrackingEta = order?.tracking?.estimatedDelivery;
    const estimatedDeliveryDate = hasTrackingEta
      ? typeof order.tracking.estimatedDelivery?.toDate === 'function'
        ? order.tracking.estimatedDelivery.toDate()
        : new Date(order.tracking.estimatedDelivery)
      : new Date(Date.now() + 5 * 24 * 60 * 60 * 1000); // default 5 business days

    const estimatedDeliveryLabel = estimatedDeliveryDate.toLocaleDateString('en-ZA', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });

    const customerName = order.customer.firstName || order.customer.lastName || 'there';
    const deliveryAddressLines = [
      order.shippingAddress?.address,
      `${order.shippingAddress?.city || ''}, ${order.shippingAddress?.province || ''}`,
      order.shippingAddress?.postalCode ? `Postal Code: ${order.shippingAddress.postalCode}` : null
    ]
      .filter(Boolean)
      .join('\n');

    const friendlyMessage = [
      `Hi ${customerName},`,
      '',
      `Thank you for placing order #${order.orderNumber} with Pontso Yarona. We are already preparing your package and will dispatch it using our standard insured courier service.`,
      '',
      `Delivery address:`,
      deliveryAddressLines,
      '',
      `Expected delivery: ${estimatedDeliveryLabel}`,
      '',
      `We will send tracking updates to ${order.customer.email} as soon as the courier collects your parcel. If you need anything in the meantime, just reply to this email or WhatsApp us on +27 84 312 9759.`,
      '',
      `With gratitude,`,
      'Pontso Yarona Customer Care'
    ].join('\n');

    const payload = {
      type: 'order_confirmation',
      orderId: order.id,
      orderNumber: order.orderNumber,
      customer: {
        email: order.customer.email,
        firstName: order.customer.firstName,
        lastName: order.customer.lastName,
      },
      totals: {
        subtotal: order.subtotal,
        shipping: order.shipping,
        tax: order.tax,
        total: order.total,
        currency: order.currency,
      },
      items: order.items,
      shippingAddress: order.shippingAddress,
      delivery: {
        method: order?.tracking?.courier || 'Standard Courier (2-5 business days)',
        expectedDateIso: estimatedDeliveryDate.toISOString(),
        expectedDateLabel: estimatedDeliveryLabel,
        trackingNumber: order?.tracking?.trackingNumber || null,
        message: friendlyMessage
      },
      metadata: {
        paymentStatus: order.paymentStatus,
        status: order.status,
        notes: order.notes || '',
        createdAt: order.createdAt instanceof Date ? order.createdAt.toISOString() : new Date().toISOString()
      },
    };

    return postEmailRequest('/emails/order-confirmation', payload);
  },

  /**
   * Send payment failure notice to customer.
   * @param {object} order - The order payload returned from OrderService.
   */
  async sendPaymentFailure(order) {
    if (!order?.customer?.email) {
      console.warn('[EmailService] Missing customer email on order; skipping failure email.');
      return { skipped: true };
    }

    const payload = {
      type: 'payment_failed',
      orderId: order.id,
      orderNumber: order.orderNumber,
      customer: {
        email: order.customer.email,
        firstName: order.customer.firstName,
        lastName: order.customer.lastName,
      },
    };

    return postEmailRequest('/emails/payment-failed', payload);
  },
};

export default EmailService;

